import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { AppShell } from '@/modules/navigation'
import { isAffiliateAllowed } from '../services/authService'
import { isMasterByEmail, normalizeRole } from '@/shared/utils/role'
import { getCurrentPlanForUser, getLatestStripeSubscriptionSyncCandidate } from '@/modules/plans/services/memberPlanService'
import { ensureFreemiumAccess } from '@/modules/plans/services/accessStateService'
import { computePaidOk } from '@/modules/auth/utils/subscriptionGate'
import { markSubscriptionSyncAttempt, shouldAttemptSubscriptionSync } from '@/modules/auth/utils/subscriptionSync'

export default function Protected({ children, roles }: { children: React.ReactNode; roles?: Array<'master'|'member'|'afiliado'> }) {
  const { user, profile, loading } = useAuth()
  const loc = useLocation()

  // Affiliate suspension gate (unconditional)
  const [affChecked, setAffChecked] = React.useState(false)
  const [affAllowed, setAffAllowed] = React.useState(true)
  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!user?.id) { setAffChecked(true); setAffAllowed(true); return }
      const roleNorm0 = (profile?.role || '').toLowerCase()
      const effectiveRole0 = roleNorm0 === 'affiliate' ? 'afiliado' : (profile?.role as any)
      if (effectiveRole0 !== 'afiliado') { setAffChecked(true); setAffAllowed(true); return }
      try {
        const ok = await isAffiliateAllowed(user.id)
        if (!cancelled) { setAffAllowed(ok); setAffChecked(true) }
      } catch {
        if (!cancelled) { setAffAllowed(true); setAffChecked(true) }
      }
    })()
    return () => { cancelled = true }
  }, [user?.id, profile?.role])

  // Precompute user-meta role (safe when user is null)
  const userMetaRoleEarly = normalizeRole(String(((user as any)?.user_metadata?.role) || ((user as any)?.app_metadata?.role) || '').trim().toLowerCase())

  // Payment gate state (declare before any early returns)
  const [paidChecked, setPaidChecked] = React.useState(false)
  const [paidOk, setPaidOk] = React.useState(true)
  const [gatePlanRef, setGatePlanRef] = React.useState<string | null>(null)
  const [gatePlanInterval, setGatePlanInterval] = React.useState<'month'|'year'|null>(null)
  const freemiumEnsureRef = React.useRef(false)
  React.useEffect(() => { freemiumEnsureRef.current = false }, [user?.id])

  // Compute intent to gate member
  const wantsMember = !!(roles && roles.includes('member'))
  const effectiveRoleStr = normalizeRole((profile?.role || '').toLowerCase())
  const isMemberEffective = effectiveRoleStr === 'member' || userMetaRoleEarly === 'member'
  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      // Only run when member route and user is member
      if (!user?.id || !wantsMember || !isMemberEffective) { setPaidChecked(true); setPaidOk(true); return }
      try {
        const applyGateState = (cur: any) => {
          const stripeSubscriptionId = String(cur.subscription?.stripe_subscription_id || '').trim() || null
          const access = (cur as any)?.access || null
          const freemiumOverride =
            !!access?.overrideActive && String(access?.effectivePlanId || '').toLowerCase() === 'trial_7d'
          const hasAny = !!cur.subscription || !!access?.effectivePlanId
          const ended = !computePaidOk(cur.subscription as any)
          const ok = freemiumOverride ? true : !ended
          const syncId =
            ended && stripeSubscriptionId && shouldAttemptSubscriptionSync(stripeSubscriptionId)
              ? stripeSubscriptionId
              : null
          if (!cancelled) {
            setPaidOk(hasAny ? ok : false)
            setPaidChecked(true)
            const planId = access?.originalPlanId || cur.plan?.id || null
            const rawInterval = ((access?.originalInterval || cur.plan?.interval || 'month') as string)
            const intervalNorm: 'month'|'year' = rawInterval === 'year' ? 'year' : 'month'
            setGatePlanRef(planId)
            setGatePlanInterval(planId ? intervalNorm : null)
          }
          return { ended, freemiumOverride, syncId }
        }

        const cur = await getCurrentPlanForUser(user.id)
        let { ended, freemiumOverride, syncId } = applyGateState(cur)
        if (!syncId) {
          const planId = String(cur.access?.effectivePlanId || cur.plan?.id || cur.subscription?.plan_id || '').toLowerCase()
          const stripeSnapshotId = String(cur.subscription?.stripe_subscription_id || '').trim()
          const isFreemiumEffective = planId === 'trial_7d'
          if (isFreemiumEffective && !stripeSnapshotId) {
            const candidateSyncId = await getLatestStripeSubscriptionSyncCandidate(user.id)
            if (candidateSyncId && shouldAttemptSubscriptionSync(candidateSyncId)) {
              syncId = candidateSyncId
            }
          }
        }
        if (syncId) {
          try {
            markSubscriptionSyncAttempt(syncId)
            const { syncSubscriptionStatus } = await import('@/modules/plans/services/memberPlanService')
            await syncSubscriptionStatus(syncId)
          } catch {}
          // Revalida estado apÃ³s sync (evita downgrade indevido quando o espelho estava desatualizado)
          try {
            const curAfter = await getCurrentPlanForUser(user.id)
            ;({ ended, freemiumOverride } = applyGateState(curAfter))
          } catch {}
        }
        if (ended && !freemiumOverride && !freemiumEnsureRef.current) {
          freemiumEnsureRef.current = true
          try { await ensureFreemiumAccess() } catch {}
          try {
            const cur3 = await getCurrentPlanForUser(user.id)
            applyGateState(cur3)
          } catch {}
        }
      } catch {
        // Prefer allow on transient error to avoid false redirect to checkout
        if (!cancelled) {
          setPaidOk(true)
          setPaidChecked(true)
          setGatePlanRef(null)
          setGatePlanInterval(null)
        }
      }
    })()
    return () => { cancelled = true }
  }, [user?.id, wantsMember, isMemberEffective])

  // Wait for session state (after declaring hooks to keep order stable)
  if (loading) return <div className="p-6 text-sm text-foreground/70">Carregando...</div>
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />

  const isMasterEmail = isMasterByEmail(user.email)
  const userMetaRole = userMetaRoleEarly

  const masterOnly = Array.isArray(roles) && roles.length === 1 && roles[0] === 'master'

  // Master-only routes
  if (masterOnly) {
    if (isMasterEmail) return <AppShell>{children}</AppShell>
    if (!profile) return <Navigate to="/access-denied" replace />
    if (profile.role === 'master') return <AppShell>{children}</AppShell>
    return <Navigate to="/access-denied" replace />
  }

  // Multi-role routes
  if (roles && roles.length > 0) {
    const roleNorm = (profile?.role || '').toLowerCase()
    // Missing profile: avoid redirecting to checkout before resolving profile/subscription
    if (!profile) {
      if (roles.includes('master') && isMasterEmail) return <AppShell>{children}</AppShell>
      if (roles.includes('afiliado') && (userMetaRole === 'afiliado')) return <AppShell>{children}</AppShell>
      if (roles.includes('member')) {
        return <AppShell><div className="p-6 text-sm text-foreground/70">Carregando...</div></AppShell>
      }
      return <Navigate to="/access-denied" replace />
    }
    // Normalize
    const effectiveRole = normalizeRole(roleNorm)
    if (effectiveRole === 'afiliado') {
      if (!affChecked) return <div className="p-6 text-sm text-foreground/70">Carregando...</div>
      if (!affAllowed) return <Navigate to="/access-denied" replace />
    }
    const ok =
      roles.includes(effectiveRole as any) ||
      (roles.includes('afiliado') && userMetaRole === 'afiliado') ||
      (roles.includes('master') && isMasterEmail) ||
      (roles.includes('member') && userMetaRole === 'member')
    if (!ok) return <Navigate to="/access-denied" replace />
    // Payment gate for member routes
    if (roles.includes('member') && (effectiveRole === 'member' || userMetaRole === 'member')) {
      if (!paidChecked) return <AppShell><div className="p-6 text-sm text-foreground/70">Carregando...</div></AppShell>
      // Regra: nunca bloquear acesso redirecionando automaticamente para checkout.
      // O downgrade/upgrade e definido por SSOT no Supabase e a renovacao deve ser iniciada pelo usuario em /my-plans.
    }
  }

  if (profile?.must_change_password && loc.pathname !== '/profile') {
    return <Navigate to="/profile" replace />
  }
  return <AppShell>{children}</AppShell>
}
